// src/components/About.jsx
import React from "react";
import { useTranslation } from "react-i18next";
import { Box, Container, Grid, Typography, Paper, Avatar, useTheme, useMediaQuery } from "@mui/material";
import { CheckCircle, Bolt, SwapHoriz, CloudUpload, TrendingUp } from "@mui/icons-material";
import { motion } from "framer-motion";

// --- ADVANCED ANIMATION VARIANTS ---

// For the main title
const titleReveal = {
  hidden: { opacity: 0, y: -20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } },
};

// For the main subtitle/description
const subtitleReveal = {
  hidden: { opacity: 0, y: 10 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut", delay: 0.2 } },
};

// For general container staggering
const staggerContainer = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.15, delayChildren: 0.2 } },
};

// For lifting and scaling cards
const cardLift = {
  hidden: { opacity: 0, y: 40, scale: 0.98 },
  visible: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } },
  whileHover: { scale: 1.03, boxShadow: "0 15px 30px rgba(0, 0, 0, 0.15)" },
};

// For small list items (faster animation)
const listItem = {
  hidden: { opacity: 0, x: -20 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.4, ease: "easeOut" } },
};

// --- Motion Component Wrappers ---
const MotionPaper = motion(Paper);
const MotionBox = motion(Box);
const MotionTypography = motion(Typography);
const MotionGrid = motion(Grid);

// Icon mapping for "How It Works" steps
const stepIcons = [
    CloudUpload, // Connect Docker Hosts
    TrendingUp,  // Real-Time Monitoring
    SwapHoriz    // Optimization & Alerts
];

const About = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const impactList = t("about.impact.list", { returnObjects: true });
  const featuresList = t("about.features.list", { returnObjects: true });
  const useCasesList = t("about.useCases.list", { returnObjects: true });
  const stepsList = t("about.howItWorks.steps", { returnObjects: true });

  return (
    <MotionBox
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      sx={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, #f3e5f5, #e0f7fa, #e8eaf6)",
        backgroundSize: "400% 400%",
        animation: "gradientAnimation 15s ease infinite",
        pt: { xs: 6, md: 10 },
        pb: { xs: 6, md: 10 },
        "& @keyframes gradientAnimation": {
          "0%": { backgroundPosition: "0% 50%" },
          "50%": { backgroundPosition: "100% 50%" },
          "100%": { backgroundPosition: "0% 50%" },
        },
      }}
    >
      <Container maxWidth="lg">
        {/* === 1. Hero Section === */}
        <Box textAlign="center" mb={{ xs: 8, md: 12 }}>
          <MotionTypography 
            variant={isMobile ? "h4" : "h3"} 
            fontWeight="bold" 
            gutterBottom
            variants={titleReveal}
            initial="hidden"
            animate="visible"
          >
            {t("about.hero.title")}
          </MotionTypography>
          <MotionTypography 
            variant={isMobile ? "body1" : "h6"} 
            color="text.secondary"
            variants={subtitleReveal}
            initial="hidden"
            animate="visible"
            maxWidth="md"
            mx="auto"
          >
            {t("about.hero.description")}
          </MotionTypography>
        </Box>

        {/* === 2. Impact & Features Sections (Side-by-Side on Desktop, Stacked on Mobile) === */}
        <MotionGrid 
            container 
            spacing={{ xs: 4, md: 8 }} 
            mb={{ xs: 8, md: 12 }}
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
        >
            {/* Impact */}
            <Grid item xs={12} md={6}>
              <MotionPaper 
                elevation={6} 
                variants={cardLift}
                whileHover="whileHover"
                sx={{ p: { xs: 3, md: 5 }, height: "100%", borderRadius: 3, cursor: 'pointer' }}
              >
                <Typography variant="h5" fontWeight="bold" mb={3} color="primary.dark">
                  {t("about.impact.title")}
                </Typography>
                <MotionBox component="ul" sx={{ listStyle: "none", p: 0, m: 0 }} variants={staggerContainer}>
                  {impactList.map((item, idx) => (
                    <MotionBox
                      component="li"
                      key={idx}
                      display="flex"
                      alignItems="flex-start" // Align start for multiline text
                      mb={2}
                      variants={listItem}
                    >
                      <CheckCircle sx={{ color: "success.main", mr: 2, mt: 0.2, flexShrink: 0 }} />
                      <Typography variant="body1" color="text.secondary">{item}</Typography>
                    </MotionBox>
                  ))}
                </MotionBox>
              </MotionPaper>
            </Grid>

            {/* Features */}
            <Grid item xs={12} md={6}>
              <MotionPaper 
                elevation={6} 
                variants={cardLift}
                whileHover="whileHover"
                sx={{ p: { xs: 3, md: 5 }, height: "100%", borderRadius: 3, cursor: 'pointer' }}
              >
                <Typography variant="h5" fontWeight="bold" mb={3} color="primary.dark">
                  {t("about.features.title")}
                </Typography>
                <MotionBox component="ul" sx={{ listStyle: "none", p: 0, m: 0 }} variants={staggerContainer}>
                  {featuresList.map((item, idx) => (
                    <MotionBox
                      component="li"
                      key={idx}
                      display="flex"
                      alignItems="flex-start" // Align start for multiline text
                      mb={2}
                      variants={listItem}
                    >
                      <Bolt sx={{ color: "primary.main", mr: 2, mt: 0.2, flexShrink: 0 }} />
                      <Typography variant="body1" color="text.secondary">{item}</Typography>
                    </MotionBox>
                  ))}
                </MotionBox>
              </MotionPaper>
            </Grid>
        </MotionGrid>

        {/* === 3. Use Cases (Card Grid) === */}
        <Box mb={{ xs: 8, md: 12 }} textAlign="center">
          <MotionTypography 
            variant={isMobile ? "h5" : "h4"} 
            fontWeight="bold" 
            mb={{ xs: 4, md: 6 }}
            variants={titleReveal}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
          >
            {t("about.useCases.title")}
          </MotionTypography>
          
          <MotionGrid 
            container 
            spacing={{ xs: 4, md: 6 }}
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
          >
            {useCasesList.map((useCase, idx) => (
              <Grid item xs={12} md={4} key={idx}>
                <MotionPaper 
                  elevation={6} 
                  variants={cardLift}
                  whileHover="whileHover"
                  sx={{ p: { xs: 3, md: 4 }, height: "100%", borderRadius: 3, cursor: 'pointer' }}
                >
                  <Typography variant="h6" fontWeight="bold" mb={2}>
                    {useCase.title}
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    {useCase.desc}
                  </Typography>
                </MotionPaper>
              </Grid>
            ))}
          </MotionGrid>
        </Box>

        {/* === 4. How It Works (Step-by-Step) === */}
        <MotionPaper 
            elevation={6} 
            sx={{ p: { xs: 4, md: 6 }, borderRadius: 3 }}
            variants={cardLift}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
        >
          <Typography 
            variant={isMobile ? "h5" : "h4"} 
            fontWeight="bold" 
            mb={{ xs: 4, md: 6 }} 
            textAlign="center"
          >
            {t("about.howItWorks.title")}
          </Typography>
          
          <MotionBox 
            display="flex" 
            flexDirection="column" 
            gap={{ xs: 4, md: 6 }}
            variants={staggerContainer} // Stagger steps
          >
            {stepsList.map((step, idx) => {
                const Icon = stepIcons[idx] || SwapHoriz; // Get correct icon
                return (
                <MotionBox variants={listItem} key={idx}>
                  <Box display="flex" alignItems="flex-start">
                    <Avatar
                      sx={{
                        bgcolor: "primary.main",
                        width: 48,
                        height: 48,
                        fontWeight: "bold",
                        mr: 3,
                        fontSize: 24
                      }}
                    >
                      <Icon sx={{ fontSize: 24 }} />
                    </Avatar>
                    <Box>
                      <Typography variant="h6" fontWeight="bold">
                        {step.title}
                      </Typography>
                      <Typography variant="body1" color="text.secondary" mt={0.5}>
                        {step.desc}
                      </Typography>
                    </Box>
                  </Box>
                </MotionBox>
                )
            })}
          </MotionBox>
        </MotionPaper>
      </Container>
    </MotionBox>
  );
};

export default About;
